import { localRecordingToolPermissionsDeniedState, localRecordingToolState } from "../../components/model";
import { v4 as uuid } from "uuid";
import { fetchDevices, getMediaStream, hasPermission } from "./recordingToolUtils";
import { calculateRecommendedVideoBitrate } from "../../../common/videoQualityUtils";
import type { RecorderLoggingData } from "../common/logging/recorderClientLogging";
import { RecorderLoggingEventType } from "../common/logging/recorderClientLogging";
import { RecorderError } from "./recorderError";

const RECORDER_MIME_TYPE = "video/mp4; codecs=\"avc1.640033, mp4a.40.2\"";
const RECORDER_AUDIO_BITS_PER_SECOND = 192_000;

const VIDEO_TRACK_CONSTRAINTS: MediaTrackConstraints = {
    frameRate: 25,
    width: { ideal: 1920 },
    height: { ideal: 1080 }
};

const AUDIO_TRACK_CONSTRAINTS: MediaTrackConstraints = {
    channelCount: 2,
    noiseSuppression: false,
    echoCancellation: false,
    sampleRate: { ideal: 48_000 }
};

export type MediaStreamType = "screen" | "camera";

export type TypedMediaStream = MediaStream & { type: MediaStreamType };

export enum RecordingState {
    Started = "SESSION_STARTED",
    Recording = "RECORDING",
    Paused = "PAUSED",
    Ended = "SESSION_ENDED",
    Error = "ERROR"
}

export interface IRecordingToolSession {
    readonly sessionId: string;
    readonly type: MediaStreamType;
    readonly currentCameraDevice?: MediaDeviceInfo;
    readonly currentMicDevice?: MediaDeviceInfo;
    getMediaStream(type?: MediaStreamType): MediaStream | undefined;
    startRecording(): void;
    stopRecording(): void;
    pauseRecording(): void;
    resumeRecording(): void;
    restartRecording(): void;
    startCapture(attemptAlternativeDevice?: boolean): Promise<TypedMediaStream[]>;
    destroy(): void;
}

export class RecordingToolSingleSession implements IRecordingToolSession {
    readonly #sessionId: string;
    readonly #type: MediaStreamType;
    #mediaStream?: MediaStream;
    #mediaRecorder?: MediaRecorder;
    #chunks: BlobPart[] = [];
    #currentCameraDevice?: MediaDeviceInfo;
    #currentMicDevice?: MediaDeviceInfo;
    readonly #onStopCallback: (blob: Blob) => void;
    readonly #onErrorCallback?: (e: Error | ErrorEvent, recorderData: RecorderLoggingData) => void;

    constructor(
        type: MediaStreamType,
        onStopCallback: (blob: Blob) => void,
        onErrorCallback?: (e: Error | ErrorEvent, recorderData: RecorderLoggingData) => void,
        currentCameraDevice?: MediaDeviceInfo,
        currentMicDevice?: MediaDeviceInfo
    ) {
        this.#sessionId = uuid();
        this.#type = type;
        this.#onStopCallback = onStopCallback;
        this.#onErrorCallback = onErrorCallback;
        this.#currentCameraDevice = currentCameraDevice;
        this.#currentMicDevice = currentMicDevice;

        localRecordingToolState(RecordingState.Started);
    }

    public get sessionId(): string {
        return this.#sessionId;
    }

    public get type(): MediaStreamType {
        return this.#type;
    }

    public get currentCameraDevice(): MediaDeviceInfo | undefined {
        return this.#currentCameraDevice;
    }

    public get currentMicDevice(): MediaDeviceInfo | undefined {
        return this.#currentMicDevice;
    }

    #attemptAlternativeDevices =
        async (streamOptions: { video: MediaTrackConstraints, audio: MediaTrackConstraints }): Promise<MediaStream> => {

            let cameras: MediaDeviceInfo[];

            try {
                cameras = (await fetchDevices()).cameras;
            }
            catch (err) {
                localRecordingToolState(RecordingState.Error);
                throw new RecorderError(
                    "Failed to fetch or initialize devices",
                    RecorderLoggingEventType.FailedToFetchMediaDevices,
                    err as Error
                );
            }

            // Try each camera until one works
            for (const camera of cameras) {
                try {
                    return await getMediaStream(
                        this.type,
                        {
                            ...streamOptions,
                            video: { ...streamOptions.video, deviceId: camera.deviceId }
                        }
                    );
                }
                catch (err: any) {
                    if (err?.name === "NotReadableError") {
                        continue; // Try the next camera
                    }

                    // For other errors, end recording and break out of the loop
                    if (err?.name !== "NotReadableError") {
                        localRecordingToolState(RecordingState.Error);
                        throw new RecorderError(
                            "Failed to get media stream",
                            RecorderLoggingEventType.FailedToGetMediaStream,
                            err as Error
                        );
                    }
                }
            }

            // If we've gone through all cameras without returning, exit with error
            localRecordingToolState(RecordingState.Error);
            this.destroy();
            throw new RecorderError(
                "All cameras are in use or unavailable",
                RecorderLoggingEventType.NoDeviceAvailable
            );
        };

    #initializeCapture = async (attemptAlternativeDevice: boolean = true): Promise<MediaStream> => {
        const constraints: { video: MediaTrackConstraints, audio: MediaTrackConstraints } = {
            video: { ...VIDEO_TRACK_CONSTRAINTS, deviceId: this.type === "camera" ? this.#currentCameraDevice?.deviceId : undefined },
            audio: { ...AUDIO_TRACK_CONSTRAINTS, deviceId: this.type === "camera" ? this.#currentMicDevice?.deviceId : undefined }
        };

        try {
            return await getMediaStream(this.type, constraints);
        }
        catch (err) {
            if (err?.name === "NotReadableError" && attemptAlternativeDevice) {
                // Reach here only in Windows and if the camera is in use
                return await this.#attemptAlternativeDevices(constraints);
            }

            if (this.type === "camera" && err?.name === "NotAllowedError") {
                localRecordingToolState(RecordingState.Error);
                // Handle and throw an error
                await this.#handleNotAllowedError();
            }

            // Handle other types of errors
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Recording failed to start",
                RecorderLoggingEventType.FailedToGetMediaStream,
                err as Error
            );
        }
    };

    #initializeRecorder = () => {
        if (!this.#mediaStream) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to initialize recorder",
                RecorderLoggingEventType.RecordingFailedToInitialize
            );
        }

        const mediaTrackSettings = this.#mediaStream.getVideoTracks()[0].getSettings();
        const { width, height, frameRate } = mediaTrackSettings;

        const options: MediaRecorderOptions = {
            mimeType: RECORDER_MIME_TYPE,
            videoBitsPerSecond: calculateRecommendedVideoBitrate(width, height, frameRate),
            audioBitsPerSecond: RECORDER_AUDIO_BITS_PER_SECOND
        };

        try {
            this.#mediaRecorder = new MediaRecorder(this.#mediaStream, options);

            this.#mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.#chunks.push(event.data);
                }
            };

            this.#mediaRecorder.onstop = () => {
                const blob = new Blob(this.#chunks, { type: options.mimeType });
                this.#onStopCallback(blob);
                this.destroy();
                localRecordingToolState(RecordingState.Ended);
            };

            this.#mediaRecorder.onerror = (error: ErrorEvent) => {
                localRecordingToolState(RecordingState.Error);
                this.destroy();
                this.#onErrorCallback?.(error,
                    {
                        recorderEventType: RecorderLoggingEventType.FailedToRecord,
                        metadata: {
                            sessionId: this.#sessionId,
                            state: localRecordingToolState(),
                            stream: this.#mediaStream,
                            type: this.#type
                        }
                    }
                );
            };
        }
        catch (err) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to initialize recorder",
                RecorderLoggingEventType.RecordingFailedToInitialize,
                err as Error
            );
        }
    };

    #cleanupRecorder = () => {
        if (this.#mediaRecorder) {
            this.#mediaRecorder.onstop = null;
            this.#mediaRecorder.ondataavailable = null;
            this.#mediaRecorder.stop();

            this.#chunks = [];

            this.#mediaRecorder = null;
        }
    };

    #cleanupMediaStream = () => {
        if (this.#mediaStream) {
            this.#mediaStream.getTracks().forEach((track) => {
                track.stop();
            });
            this.#mediaStream = null;
        }
    };

    /* Stream stopped handler. will be triggered when:
        1. There is no more data left to send.
        2. The user revoked the permissions needed for the data to be sent.
        3. The hardware generating the source data has been removed or ejected.
        4. A remote peer has permanently stopped sending data.
        5. The only case where the track ends but the ended event is not fired is when calling MediaStreamTrack.stop.
    */
    #handleStreamStoppedUnexpectedly = () => {
        const handler = () => {
            if (localRecordingToolState() === RecordingState.Started) {
                localRecordingToolState(RecordingState.Error);
                this.#mediaStream = null;
                this.#onErrorCallback?.(new Error("Stream stopped unexpectedly"),
                    {
                        recorderEventType: RecorderLoggingEventType.StreamStoppedUnexpectedly,
                        metadata: {
                            sessionId: this.#sessionId,
                            state: localRecordingToolState(),
                            type: this.#type
                        }
                    }
                );
            }
        };

        this.#mediaStream?.getTracks().forEach((track) => {
            track.onended = handler;
        });
    };

    #handleNotAllowedError = async () => {
        const isVideoPermissionGranted = await hasPermission("camera");
        const isAudioPermissionGranted = await hasPermission("microphone");

        if (isVideoPermissionGranted) {
            localRecordingToolPermissionsDeniedState("microphone");
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to start recording due to mic permissions",
                RecorderLoggingEventType.MicPermissionsDenied
            );
        }
        if (isAudioPermissionGranted) {
            localRecordingToolPermissionsDeniedState("camera");
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to start recording due to camera permissions",
                RecorderLoggingEventType.CameraPermissionsDenied
            );
        }

        localRecordingToolPermissionsDeniedState("cameraAndMicrophone");
        localRecordingToolState(RecordingState.Error);
        throw new RecorderError(
            "Failed to start recording due to camera and mic permissions",
            RecorderLoggingEventType.CameraAndMicPermissionsDenied
        );
    };

    getMediaStream(type?: MediaStreamType): MediaStream | undefined {

        if (type && type !== this.type) {
            return undefined;
        }

        return this.#mediaStream;
    }

    startRecording(): void {

        if (localRecordingToolState() !== RecordingState.Started) {
            throw new RecorderError(
                `Cannot start recording when in state ${localRecordingToolState()}`,
                RecorderLoggingEventType.InvalidStateError
            );
        }

        if (!this.#mediaRecorder) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "No active recorder to start",
                RecorderLoggingEventType.StartRecordingError
            );
        }

        try {
            this.#mediaRecorder.start();
            localRecordingToolState(RecordingState.Recording);
        }
        catch (err) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to start recording",
                RecorderLoggingEventType.StartRecordingError,
                err as Error
            );
        }
    }

    stopRecording(): void {

        if (localRecordingToolState() !== RecordingState.Recording && localRecordingToolState() !== RecordingState.Paused) {
            throw new RecorderError(
                `Cannot stop recording when in state ${localRecordingToolState()}`,
                RecorderLoggingEventType.InvalidStateError
            );
        }

        if (!this.#mediaRecorder) {
            throw new RecorderError(
                "No active recorder to stop",
                RecorderLoggingEventType.StopRecordingError
            );
        }


        if (this.#mediaRecorder.state === "inactive") {
            this.destroy();
            localRecordingToolState(RecordingState.Ended);
            return;
        }
        else {
            try {
                this.#mediaRecorder.stop();
            }
            catch (err) {
                localRecordingToolState(RecordingState.Error);
                throw new RecorderError(
                    "Failed to stop recording",
                    RecorderLoggingEventType.StopRecordingError,
                        err as Error
                );
            }
        }
    }

    pauseRecording(): void {

        if (localRecordingToolState() !== RecordingState.Recording) {
            throw new RecorderError(
                `Cannot pause recording when in state ${localRecordingToolState()}`,
                RecorderLoggingEventType.InvalidStateError
            );
        }

        if (!this.#mediaRecorder) {
            throw new RecorderError(
                "No active recorder to pause",
                RecorderLoggingEventType.PauseRecordingError
            );
        }

        try {
            this.#mediaRecorder.pause();
            localRecordingToolState(RecordingState.Paused);
        }
        catch (err) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to pause recording",
                RecorderLoggingEventType.PauseRecordingError,
                err as Error
            );
        }
    }

    resumeRecording(): void {

        if (!this.#mediaRecorder) {
            throw new RecorderError(
                "No active recorder to resume",
                RecorderLoggingEventType.ResumeRecordingError
            );
        }

        try {
            this.#mediaRecorder.resume();
            localRecordingToolState(RecordingState.Recording);
        }
        catch (err) {
            localRecordingToolState(RecordingState.Error);
            throw new RecorderError(
                "Failed to resume recording",
                RecorderLoggingEventType.ResumeRecordingError,
                err as Error
            );
        }
    }

    restartRecording(): void {
        this.#cleanupRecorder();
        this.#initializeRecorder();
        localRecordingToolState(RecordingState.Started);
    }

    async startCapture(attemptAlternativeDevice: boolean = true): Promise<TypedMediaStream[]> {
        localRecordingToolState(RecordingState.Started);
        if (this.#mediaRecorder) {
            this.#cleanupRecorder();
        }

        const stream = await this.#initializeCapture(attemptAlternativeDevice);
        this.#mediaStream = stream;

        if (this.type === "camera" && this.#mediaStream) {
            try {
                const { cameras, mics } = await fetchDevices();
                this.#currentCameraDevice = cameras.find((camera) => camera.deviceId === this.#mediaStream.getTracks().find(track => track.kind === "video").getSettings().deviceId);
                this.#currentMicDevice = mics.find((mic) => mic.deviceId === this.#mediaStream.getTracks().find(track => track.kind === "audio").getSettings().deviceId);
            }
            catch (e) { /* Do not throw an error if we fail to fetch devices */ }
        }

        this.#handleStreamStoppedUnexpectedly();
        this.#initializeRecorder();

        const typed = Object.assign(stream, { type: this.type }) as TypedMediaStream;
        return [typed];
    }

    destroy(): void {
        this.#cleanupRecorder();
        this.#cleanupMediaStream();
    }
}

export class RecordingToolDualSession implements IRecordingToolSession {
    readonly #sessionId: string;
    readonly #type: MediaStreamType;

    public get sessionId(): string {
        return this.#sessionId;
    }

    public get type(): MediaStreamType {
        return this.#type;
    }

    getMediaStream(type?: MediaStreamType): MediaStream | undefined {
        return undefined;
    }

    pauseRecording(): void {
        return undefined;
    }

    resumeRecording(): void {
        return undefined;
    }

    restartRecording(): Promise<void> {
        return undefined;
    }

    startCapture(): Promise<TypedMediaStream[]> {
        return undefined;
    }

    startRecording(): void {
        return undefined;
    }

    stopRecording(): void {
        return undefined;
    }

    destroy(): void {

    }
}
